import { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useDragControls, useMotionValue, useTransform } from 'framer-motion'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import {
  dismissHelperNotification,
  openHelperNotification,
  restoreHelperNotification,
  subscribeToHelperNotifications,
  type HelperNotificationItem,
} from '../../services/helper/helperNotificationService'
import { getHelperSnapshot, requestHelperWorkspaceSwitch } from '../../services/helper/helperStore'
import { openWorkspaceTarget } from '../../services/workspaceSwitch/openWorkspaceTarget'
import { useGlobalActionLoading } from '../../context/useGlobalActionLoading'
import { ToastContext } from '../../context/toastContextCore'
import { usePushBackHandler } from '../../context/BackStackContext'
import { ConfirmActionModal } from '../Common/ConfirmActionModal'
import { NotificationUndoSnackbar, type UndoState } from '../Common/NotificationUndoSnackbar'
import { Button, Pressable } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import './HelperNotificationDrawer.css'

const MotionPressable = motion.create(Pressable)

const formatRelative = (ms: number | null): string => {
  if (!ms) return ''
  const delta = Date.now() - ms
  if (delta < 60_000) return 'now'
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m`
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h`
  return `${Math.floor(delta / 86_400_000)}d`
}

const KIND_ICON: Record<NonNullable<HelperNotificationItem['deeplink']>['kind'], typeof TaskAltRoundedIcon> = {
  CHECKLIST: TaskAltRoundedIcon,
  MEETING: CalendarMonthRoundedIcon,
  CHAT: ForumRoundedIcon,
  WORKSPACE: BusinessRoundedIcon,
  INVITE: PersonAddAltRoundedIcon,
  UNKNOWN: NotificationsRoundedIcon,
} as const

type Props = {
  open: boolean
  onClose: () => void
  uid: string | null
  onNavigate: (item: HelperNotificationItem) => void
  onOpenNotificationSettings: () => void
  hide?: (item: HelperNotificationItem) => boolean
}

type RowProps = {
  item: HelperNotificationItem
  busy: boolean
  onSelect: () => void
  onDismiss: () => void
}

const NotificationRow = ({ item, busy, onSelect, onDismiss }: RowProps) => {
  const { t } = useNls()
  const ref = useRef<HTMLDivElement>(null)
  const dragControls = useDragControls()
  const x = useMotionValue(0)
  const opacity = useTransform(x, [-180, -24], [1, 0])
  const dragged = useRef(false)
  const Icon = KIND_ICON[item.deeplink?.kind ?? 'UNKNOWN']

  return (
    <div ref={ref} className="helperNotificationRowWrap">
      <motion.div className="helperNotificationDismiss" style={{ opacity }}>
        <DeleteRoundedIcon />
        <span>{t('NLSHelperNotificationDismiss')}</span>
      </motion.div>
      <MotionPressable
        drag={busy ? false : 'x'}
        dragControls={dragControls}
        dragConstraints={{ left: -320, right: 0 }}
        dragElastic={0.08}
        style={{ x }}
        onPointerDown={(event) => {
          if (!busy) dragControls.start(event)
        }}
        onDragStart={() => {
          dragged.current = true
        }}
        onDragEnd={(_, info) => {
          if (Math.abs(info.offset.x) >= (ref.current?.getBoundingClientRect().width ?? 1) * 0.4) onDismiss()
          else x.set(0)
          window.setTimeout(() => {
            dragged.current = false
          }, 0)
        }}
        onClick={() => {
          if (!dragged.current && !busy) onSelect()
        }}
        disabled={busy}
        className={`helperNotificationRow${item.readAt ? '' : ' isUnread'}`}
      >
        <span className="helperNotificationGlyph">
          <Icon />
          {!item.readAt ? <i /> : null}
        </span>
        <span className="helperNotificationCopy">
          <span className="helperNotificationTitle">{item.title}</span>
          <span className="helperNotificationWorkspace text-[11px] text-[var(--muted)]">
            {item.workspaceId ? (item.workspaceName || item.workspaceId) : t('NLSNotificationRowAccount')}
          </span>
          {item.body ? <span className="helperNotificationBody">{item.body}</span> : null}
        </span>
        <span className="helperNotificationMeta">
          {formatRelative(item.createdAt)}
          <ChevronRightRoundedIcon />
        </span>
      </MotionPressable>
    </div>
  )
}

const isHelperInviteNotification = (item: HelperNotificationItem): boolean => {
  if (item.deeplink?.kind === 'INVITE') return true
  if (item.type === 'INVITE_RECEIVED' || item.eventType === 'INVITE_RECEIVED') return true
  const refId = item.deeplink?.refId
  if (typeof refId === 'string' && (refId.includes('/invite-review/') || refId.includes('/join'))) return true
  return false
}

export const HelperNotificationDrawer = ({ open, onClose, uid, onNavigate, onOpenNotificationSettings, hide }: Props) => {
  usePushBackHandler(onClose, open)
  const { t } = useNls()
  const toastCtx = useContext(ToastContext)
  const showToast = toastCtx?.showToast
  const [items, setItems] = useState<HelperNotificationItem[]>([])
  const { runWithGlobalLoading } = useGlobalActionLoading()
  const [undo, setUndo] = useState<UndoState | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const [prevUid, setPrevUid] = useState(uid)
  if (uid !== prevUid) {
    setPrevUid(uid)
    if (!uid && items.length > 0) {
      setItems([])
    }
  }

  useEffect(() => {
    if (!uid) return
    return subscribeToHelperNotifications(uid, setItems, { hide })
  }, [uid, hide])

  const clearEntries = async (entries: HelperNotificationItem[]) => {
    if (!uid || !entries.length) return
    await Promise.all(entries.map((entry) => dismissHelperNotification(uid, entry.id)))
    setUndo({
      ids: entries.map((entry) => entry.id),
      message: entries.length === 1 ? t('NLSHelperNotificationClearedSingle') : t('NLSHelperNotificationClearedMany', { count: entries.length }),
    })
  }

  const handleDismiss = async (item: HelperNotificationItem) => {
    setBusyId(item.id)
    try {
      await clearEntries([item])
    } finally {
      setBusyId(null)
    }
  }

  const handleRestore = async () => {
    const current = undo
    setUndo(null)
    if (uid && current) await restoreHelperNotification(uid, current.ids).catch(() => undefined)
  }

  const handleClearAll = async () => {
    setConfirmBusy(true)
    try {
      await clearEntries(items)
      setConfirmOpen(false)
    } finally {
      setConfirmBusy(false)
    }
  }

  const handleSelect = async (item: HelperNotificationItem) => {
    if (!uid) return
    await runWithGlobalLoading(async (signal) => {
      if (item.workspaceId && !isHelperInviteNotification(item)) {
        const helperSnap = getHelperSnapshot()
        const switched = await openWorkspaceTarget({
          workspaceId: item.workspaceId,
          source: 'DRAWER',
          options: {
            activeWorkspaceId: helperSnap.activeWorkspaceId,
            workspaces: helperSnap.workspaces,
            workspaceName: item.workspaceName,
            switchWorkspace: async (targetId) => {
              return await requestHelperWorkspaceSwitch(targetId)
            },
            showToast,
            t,
          },
        })
        if (!switched) return
      }
      onNavigate(item)
      onClose()
      if (signal.aborted) return
      void openHelperNotification(uid, item.id).catch(() => undefined)
    }, {
      label: t('NLSHelperNotificationOpeningLabel'),
      messages: [
        t('NLSHelperNotificationOpeningMsg1'),
        t('NLSHelperNotificationOpeningMsg2'),
        t('NLSHelperNotificationOpeningMsg3'),
      ],
    })
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <MotionPressable
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="helperNotificationScrim"
            onClick={onClose}
            aria-label={t('NLSHelperNotificationCloseAria')}
          />
          {/* eslint-disable-next-line no-restricted-syntax -- helper notification slide-in drawer uses custom spring motion and is not a Common modal */}
          <motion.aside role="dialog"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="helperNotificationDrawer"
            aria-label={t('NLSHelperNotificationDrawerAria')}
            data-testid="helper-notification-drawer"
          >
            <header className="helperNotificationHeader">
              <p>{t('NLSHelperNotificationHeader')}</p>
              <Button
                variant="tertiary"
                size="md"
                onClick={() => (items.length > 5 ? setConfirmOpen(true) : void clearEntries(items))}
                disabled={items.length === 0}
              >
                {t('NLSHelperNotificationClearAll')}
              </Button>
            </header>
            <div className="helperNotificationList">
              {items.length === 0 ? (
                <div className="helperNotificationEmpty">
                  <TaskAltRoundedIcon />
                  {t('NLSHelperNotificationAllCaughtUp')}
                </div>
              ) : (
                items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    busy={busyId === item.id}
                    onSelect={() => void handleSelect(item)}
                    onDismiss={() => void handleDismiss(item)}
                  />
                ))
              )}
            </div>
            <footer className="helperNotificationFooter">
              <Button
                variant="tertiary"
                size="md"
                onClick={onOpenNotificationSettings}
                icon={<SettingsRoundedIcon />}
                trailingIcon={<ChevronRightRoundedIcon />}
              >
                {t('NLSHelperNotificationSettings')}
              </Button>
            </footer>
            <NotificationUndoSnackbar undo={undo} onUndo={() => void handleRestore()} onDismiss={() => setUndo(null)} />
          </motion.aside>
          <ConfirmActionModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => void handleClearAll()}
            title={t('NLSHelperNotificationConfirmTitle')}
            body={t('NLSHelperNotificationConfirmBody')}
            confirmLabel={t('NLSHelperNotificationClearAll')}
            danger
            busy={confirmBusy}
          />
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
